/**
 * Settings page — API key management for supported Thread API access.
 *
 * Users can generate, view, and revoke API keys from this page.
 * Generated keys are shown once and cannot be retrieved again.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Dialog,
  Flex,
  Heading,
  Select,
  Table,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";

import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  type ApiKey,
  type ApiKeyScope,
  type CreateKeyPayload,
  type CreatedApiKey,
} from "../api/apiKeys";

type SettingsKeyScope = Extract<ApiKeyScope, "full" | "bobbin_daily">;

export function settingsKeyPayload(
  name: string,
  flow360ApiKey: string,
  scope: SettingsKeyScope,
): CreateKeyPayload {
  if (scope === "bobbin_daily") {
    return { name, scope };
  }
  return {
    name,
    scope,
    flow360_api_key: flow360ApiKey || undefined,
  };
}

export function bobbinDailyApiOrigin(
  configuredApiUrl: string | undefined,
  browserOrigin: string,
): string {
  try {
    const url = new URL(configuredApiUrl || browserOrigin, browserOrigin);
    const basePath = url.pathname.replace(/\/api\/?$/, "").replace(/\/$/, "");
    return `${url.origin}${basePath}`;
  } catch {
    return browserOrigin.replace(/\/$/, "");
  }
}

export function bobbinDailyCurlExamples(apiOrigin: string): string {
  const origin = apiOrigin.replace(/\/$/, "");
  return `export BOBBIN_DAILY_API_ORIGIN="${origin}"
export BOBBIN_DAILY_KEY='<paste the key shown above>'
export BOBBIN_DAILY_DATE="$(date +%F)"
export BOBBIN_DAILY_OPERATION_ID="mobile-$(uuidgen | tr '[:upper:]' '[:lower:]')"

# Read a Daily
curl -fsS -H "Authorization: Bearer $BOBBIN_DAILY_KEY" "$BOBBIN_DAILY_API_ORIGIN/api/spool/daily/$BOBBIN_DAILY_DATE"

# Append a section (reuse the idempotency key only for retries)
curl -fsS -X POST -H "Authorization: Bearer $BOBBIN_DAILY_KEY" -H "Content-Type: application/json" "$BOBBIN_DAILY_API_ORIGIN/api/spool/daily/sections" --data @- <<JSON
{"idempotency_key":"$BOBBIN_DAILY_OPERATION_ID","entry_date":"$BOBBIN_DAILY_DATE","heading":"Notes","body_md":"Follow up with Jenny."}
JSON

# Update a section (replace UUID and version with values returned by GET)
curl -fsS -X PATCH -H "Authorization: Bearer $BOBBIN_DAILY_KEY" -H "Content-Type: application/json" "$BOBBIN_DAILY_API_ORIGIN/api/spool/daily/sections/SECTION_UUID" --data @- <<JSON
{"entry_date":"$BOBBIN_DAILY_DATE","expected_version":1,"body_md":"Followed up with Jenny."}
JSON`;
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  if (s < 604800) return `${Math.floor(s / 86400)} days ago`;
  return `${Math.floor(s / 604800)} weeks ago`;
}

export function apiKeyExpiryLabel(
  expiresAt: string | null | undefined,
  nowMs = Date.now(),
): string {
  if (!expiresAt) return "Never";
  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "Expired";
  const days = Math.ceil(remainingMs / 86_400_000);
  return days === 1 ? "1 day" : `${days} days`;
}

export function apiKeyCredentialLabel(key: Pick<ApiKey, "key_prefix" | "scope">): string {
  if (key.scope === "bobbin_mobile") return "Protected session";
  return key.key_prefix ? `${key.key_prefix}...` : "Unavailable";
}

export default function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dailyApiOrigin = bobbinDailyApiOrigin(
    import.meta.env.VITE_API_URL,
    typeof window === "undefined" ? "http://127.0.0.1:8000" : window.location.origin,
  );

  const { data: keys, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: fetchApiKeys,
  });

  const [newKeyName, setNewKeyName] = useState("Default");
  const [flow360Key, setFlow360Key] = useState("");
  const [newKeyScope, setNewKeyScope] = useState<SettingsKeyScope>("full");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createApiKey(settingsKeyPayload(newKeyName, flow360Key, newKeyScope)),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setShowCreateDialog(false);
      setCreatedKey(data);
      setNewKeyName("Default");
      setFlow360Key("");
      setNewKeyScope("full");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setRevokeTarget(null);
    },
  });

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box p="5" style={{ maxWidth: 800, margin: "0 auto" }}>
      <Flex justify="between" align="center" mb="2">
        <Button
          variant="ghost"
          size="1"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          &larr; Back
        </Button>
      </Flex>

      <Heading size="6" mb="1">
        Settings
      </Heading>
      <Text color="gray" size="2" mb="5" as="p">
        Manage bearer keys for supported Thread API clients.
      </Text>

      {/* API Keys Section */}
      <Card mb="5">
        <Flex justify="between" align="center" mb="4">
          <div>
            <Heading size="4">API Keys &amp; mobile devices</Heading>
            <Text color="gray" size="2" as="p">
              Keys authenticate supported Thread API requests. The repository
              Python SDK currently supports local capture examples; it does not
              expose a general native V3 cloud writer.
            </Text>
          </div>
          <Dialog.Root
            open={showCreateDialog}
            onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (open) createMutation.reset();
            }}
          >
            <Dialog.Trigger>
              <Button size="2">Generate new key</Button>
            </Dialog.Trigger>
            <Dialog.Content maxWidth="480px">
              <Dialog.Title>Generate API Key</Dialog.Title>
              <Dialog.Description size="2" color="gray" mb="4">
                Choose the narrowest access the client needs, then give the key
                a name so you can identify and revoke it later.
              </Dialog.Description>

              <Flex direction="column" gap="3">
                <label>
                  <Text size="2" weight="medium" mb="1" as="p">
                    Access
                  </Text>
                  <Select.Root
                    value={newKeyScope}
                    onValueChange={(value) => {
                      const nextScope = value as SettingsKeyScope;
                      setNewKeyScope(nextScope);
                      setNewKeyName((current) => {
                        if (nextScope === "bobbin_daily" && current === "Default") {
                          return "Bobbin Daily agent";
                        }
                        if (nextScope === "full" && current === "Bobbin Daily agent") {
                          return "Default";
                        }
                        return current;
                      });
                    }}
                  >
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      <Select.Item value="full">Full Thread API</Select.Item>
                      <Select.Item value="bobbin_daily">
                        Bobbin Daily agent only
                      </Select.Item>
                    </Select.Content>
                  </Select.Root>
                </label>
                <label>
                  <Text size="2" weight="medium" mb="1" as="p">
                    Key name
                  </Text>
                  <TextField.Root
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g. Laptop, CI Server"
                  />
                </label>
                {newKeyScope === "full" ? (
                  <label>
                    <Text size="2" weight="medium" mb="1" as="p">
                      Flow360 API Key{" "}
                      <Text color="gray" size="1">
                        (optional)
                      </Text>
                    </Text>
                    <TextField.Root
                      value={flow360Key}
                      onChange={(e) => setFlow360Key(e.target.value)}
                      placeholder="SimCloud API key for Flow360 features"
                    />
                  </label>
                ) : (
                  <Text size="2" color="gray" as="p">
                    This revocable key expires after 30 days and can only read,
                    append, or update your Bobbin Daily. It cannot access other
                    Thread APIs or Flow360.
                  </Text>
                )}
                {createMutation.isError && (
                  <Text size="2" color="red" as="p" role="alert">
                    {createMutation.error instanceof Error
                      ? createMutation.error.message
                      : "Could not generate this API key. Please try again."}
                  </Text>
                )}
              </Flex>

              <Flex gap="3" mt="4" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  onClick={() => createMutation.mutate()}
                  disabled={!newKeyName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? "Generating..." : "Generate"}
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
        </Flex>

        {isLoading ? (
          <Text color="gray" size="2">
            Loading keys...
          </Text>
        ) : !keys?.length ? (
          <Text color="gray" size="2">
            No API keys yet. Generate one for an authenticated API client.
          </Text>
        ) : (
          <Box style={{ overflowX: "auto" }}>
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Key</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Last used</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Expires</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Flow360</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {keys.map((key) => (
                  <Table.Row key={key.id}>
                    <Table.Cell>
                      <Flex direction="column" gap="1" align="start">
                        <Text weight="medium">{key.name}</Text>
                        {key.scope === "bobbin_daily" && (
                          <Badge color="blue" size="1" variant="soft">
                            Daily only
                          </Badge>
                        )}
                        {key.scope === "bobbin_mobile" && (
                          <Badge color="green" size="1" variant="soft">
                            Bobbin mobile
                          </Badge>
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      {key.scope === "bobbin_mobile" ? (
                        <Text size="2" color="gray">
                          {apiKeyCredentialLabel(key)}
                        </Text>
                      ) : (
                        <Code size="2" color="gray">
                          {apiKeyCredentialLabel(key)}
                        </Code>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Tooltip content={new Date(key.created_at).toLocaleString()}>
                        <Text size="2" color="gray">
                          {timeAgo(key.created_at)}
                        </Text>
                      </Tooltip>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {key.last_used_at ? timeAgo(key.last_used_at) : "Never"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Tooltip
                        content={
                          key.expires_at
                            ? new Date(key.expires_at).toLocaleString()
                            : "This key does not expire automatically"
                        }
                      >
                        <Badge
                          color={
                            apiKeyExpiryLabel(key.expires_at) === "Expired"
                              ? "red"
                              : "gray"
                          }
                          size="1"
                          variant="soft"
                        >
                          {apiKeyExpiryLabel(key.expires_at)}
                        </Badge>
                      </Tooltip>
                    </Table.Cell>
                    <Table.Cell>
                      {key.has_flow360_key ? (
                        <Badge color="green" size="1">
                          Linked
                        </Badge>
                      ) : (
                        <Badge color="gray" size="1" variant="soft">
                          None
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        variant="ghost"
                        color="red"
                        size="1"
                        onClick={() => setRevokeTarget(key)}
                        style={{ cursor: "pointer" }}
                      >
                        Revoke
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Card>

      {/* API Quick Start */}
      <Card>
        <Heading size="4" mb="2">
          Quick Start
        </Heading>
        <Text size="2" color="gray" as="p" mb="3">
          Copy the generated value once, keep it out of source control, and use
          it as a bearer credential with a route from the runtime OpenAPI document:
        </Text>
        <Code
          size="2"
          variant="soft"
          style={{
            display: "block",
            padding: "12px 16px",
            whiteSpace: "pre",
            lineHeight: 1.6,
          }}
        >
          {`export THREAD_API_ORIGIN=https://thread-api-preprod.simulation.cloud\nexport THREAD_API_KEY=thr_replace_with_your_key\ncurl -fsS -H "Authorization: Bearer $THREAD_API_KEY" "$THREAD_API_ORIGIN/api/projects"`}
        </Code>
      </Card>

      {/* Created Key Dialog — shows raw key once */}
      <Dialog.Root
        open={!!createdKey}
        onOpenChange={(open) => {
          if (!open) setCreatedKey(null);
        }}
      >
        <Dialog.Content maxWidth="520px">
          <Dialog.Title>API Key Created</Dialog.Title>
          <Dialog.Description size="2" color="red" mb="4">
            Copy this key now. You will not be able to see it again.
          </Dialog.Description>

          {createdKey?.scope === "bobbin_daily" && (
            <Text size="2" color="gray" as="p" mb="3">
              This credential works only with the Bobbin Daily API and expires
              after 30 days. Revoke it here if a device or agent loses access.
            </Text>
          )}

          <Flex
            align="center"
            gap="2"
            p="3"
            style={{
              background: "var(--gray-a2)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Code
              size="2"
              style={{
                flex: 1,
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              {createdKey?.raw_key}
            </Code>
            <Button
              variant="soft"
              size="1"
              onClick={() =>
                createdKey && copyToClipboard(createdKey.raw_key)
              }
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </Flex>

          {createdKey?.scope === "bobbin_daily" && (
            <Box mt="4">
              <Heading size="3" mb="1">
                Connect a mobile agent
              </Heading>
              <Text size="2" color="gray" as="p" mb="2">
                Use this Thread API directly from any HTTPS client. The full
                schema is also published at the server&apos;s /openapi.json.
              </Text>
              <Code
                size="1"
                variant="soft"
                style={{
                  display: "block",
                  maxHeight: 300,
                  overflow: "auto",
                  padding: "12px 16px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.5,
                }}
              >
                {bobbinDailyCurlExamples(dailyApiOrigin)}
              </Code>
            </Box>
          )}

          <Flex justify="end" mt="4">
            <Dialog.Close>
              <Button>Done</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Revoke Confirmation */}
      <AlertDialog.Root
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialog.Content maxWidth="400px">
          <AlertDialog.Title>
            {revokeTarget?.scope === "bobbin_mobile"
              ? "Revoke mobile device"
              : "Revoke API Key"}
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            {revokeTarget?.scope === "bobbin_mobile" ? (
              <>
                Revoke <strong>{revokeTarget.name}</strong>? That device will
                immediately lose Bobbin access.
              </>
            ) : (
              <>
                Revoke <strong>{revokeTarget?.name}</strong> (
                <Code size="1">
                  {revokeTarget ? apiKeyCredentialLabel(revokeTarget) : ""}
                </Code>
                )? Any API clients using this key will immediately stop working.
              </>
            )}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                color="red"
                onClick={() =>
                  revokeTarget && revokeMutation.mutate(revokeTarget.id)
                }
              >
                Revoke
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Box>
  );
}
